import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import ChatMetricsPanel from "./ChatMetricsPanel";
import "./ChatPage.css";
import "./ChatFloatingWidget.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

interface MediaContent {
  type: "image" | "audio" | "video" | "document";
  url: string;
  caption?: string;
}

// Bracket pattern for legacy/gateway placeholders like "[Media message]", "[image]", "[audio]"
const BRACKET_MEDIA_RE = /^\[(media message|image|photo|audio|voice|voice ?note|video|document|file|sticker|gif)\]$/i;

function parseMediaContent(content: string): MediaContent | null {
  if (content.startsWith("{")) {
    try {
      const parsed = JSON.parse(content);
      if (parsed.type && parsed.url) return {
        type: parsed.type,
        url: parsed.url,
        caption: parsed.caption || parsed.description || undefined,
      } as MediaContent;
    } catch { /* not JSON */ }
  }
  // Handle bracket placeholders where no URL was forwarded
  const m = content.trim().match(BRACKET_MEDIA_RE);
  if (m) {
    const raw = m[1].toLowerCase();
    const type: MediaContent["type"] =
      raw.includes("image") || raw.includes("photo") || raw.includes("sticker") || raw.includes("gif") ? "image"
      : raw.includes("audio") || raw.includes("voice") ? "audio"
      : raw.includes("video") ? "video"
      : "document";
    return { type, url: "", caption: "" };
  }
  return null;
}

const MEDIA_LABELS: Record<string, string> = { audio: "Voice note", image: "Photo", video: "Video", document: "File" };

function MediaIcon({ type }: { type: string }) {
  const common = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.7, width: 16, height: 16 };
  if (type === "image") return <svg {...common}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>;
  if (type === "audio") return <svg {...common}><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>;
  if (type === "video") return <svg {...common}><rect x="2" y="5" width="14" height="14" rx="2"/><polyline points="16 9 22 6 22 18 16 15"/></svg>;
  return <svg {...common}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>;
}

function MediaUnavailable({ type }: { type: string }) {
  return (
    <div className="chat-media-unavailable">
      <MediaIcon type={type} />
      <span className="chat-media-unavailable-label">{MEDIA_LABELS[type] ?? "Media"}</span>
      <span className="chat-media-unavailable-sub">unavailable</span>
    </div>
  );
}

function ChatImage({ media, onImageClick }: { media: MediaContent; onImageClick?: (url: string, caption?: string) => void }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <MediaUnavailable type="image" />;
  return (
    <img
      src={media.url}
      alt="Photo"
      onError={() => setFailed(true)}
      onClick={() => onImageClick?.(media.url, media.caption)}
      style={{ maxWidth: "min(260px, calc(75vw - 24px))", maxHeight: 200, width: "100%", borderRadius: 8, display: "block", objectFit: "cover", cursor: "pointer" }}
    />
  );
}

function MessageMedia({ media, onImageClick }: { media: MediaContent; onImageClick?: (url: string, caption?: string) => void }) {
  // No URL = gateway sent a placeholder like "[Media message]" without a URL
  if (!media.url) return <MediaUnavailable type={media.type} />;

  if (media.type === "audio") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <audio controls style={{ maxWidth: 280, height: 36, borderRadius: 8 }} preload="metadata">
          <source src={media.url} />
        </audio>
        {media.caption && <span style={{ fontSize: 12, opacity: 0.7 }}>{media.caption}</span>}
      </div>
    );
  }
  if (media.type === "image") {
    return <ChatImage media={media} onImageClick={onImageClick} />;
  }
  if (media.type === "video") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <video controls style={{ maxWidth: 260, maxHeight: 180, borderRadius: 8 }} preload="metadata">
          <source src={media.url} />
        </video>
        {media.caption && <span style={{ fontSize: 12, opacity: 0.7 }}>{media.caption}</span>}
      </div>
    );
  }
  return (
    <a href={media.url} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
      <MediaIcon type="document" /> {media.caption || "Download file"}
    </a>
  );
}

function AlbumCell({ img }: { img: { url: string; caption?: string } }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="chat-album-fallback">
        <MediaIcon type="image" />
      </div>
    );
  }
  return <img src={img.url} alt="" className="chat-album-img" onError={() => setFailed(true)} />;
}

function AlbumGrid({ msgs, onImageClick }: { msgs: { url: string; caption?: string }[]; onImageClick?: (url: string, caption?: string) => void }) {
  const count = Math.min(msgs.length, 4);
  const extra = msgs.length - 4;
  return (
    <div className={`chat-album chat-album-${count}`}>
      {msgs.slice(0, count).map((img, i) => (
        <div key={i} className="chat-album-cell" onClick={() => onImageClick?.(img.url, img.caption)}>
          <AlbumCell img={img} />
          {i === count - 1 && extra > 0 && (
            <div className="chat-album-more">+{extra + 1}</div>
          )}
        </div>
      ))}
    </div>
  );
}

interface Session {
  id: number;
  session_id: string;
  guest_name: string | null;
  guest_phone: string | null;
  booking_id: string | null;
  source: string;
  status: "open" | "resolved" | "closed" | "needs_attention";
  unread: number;
  ai_enabled: boolean | null;
  handoff_reason: string | null;
  handoff_briefing: string | null;
  handoff_briefing_at: string | null;
  created_at: string;
  updated_at: string;
  last_message: string | null;
  last_message_role: string | null;
  room_number: string | null;
  guest_status: "inhouse" | "arriving" | null;
}

interface Message {
  id: number;
  session_id: string;
  role: "user" | "assistant" | "staff";
  content: string;
  sent_by: string | null;
  created_at: string;
}

interface GuestInfo {
  session: Session | null;
  booking: {
    bookingid: string;
    guestname: string;
    phonenumber: string;
    checkin: string;
    checkout: string;
    status: string;
    roomnumber: string | null;
  } | null;
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

function fmtDate(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString())     return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: d.getFullYear() !== today.getFullYear() ? "numeric" : undefined });
}

function sameDay(a: string, b: string) {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

function previewText(content: string | null, role: string | null) {
  if (!content) return "No messages yet";
  const prefix = role === "assistant" ? "AI: " : role === "staff" ? "You: " : "";
  const media = parseMediaContent(content);
  if (media) {
    const labels: Record<string, string> = { image: "Photo", audio: "Voice message", video: "Video", document: "File" };
    return prefix + (labels[media.type] || "Media");
  }
  const text = content.replace(/\n/g, " ").slice(0, 48);
  return prefix + (content.length > 48 ? text + "…" : text);
}

function waitingInfo(s: { last_message_role: string | null; updated_at: string; status: string }) {
  if (s.last_message_role !== "user" || s.status !== "open") return null;
  const mins = Math.round((Date.now() - new Date(s.updated_at).getTime()) / 60000);
  if (mins < 5) return null;
  return { mins, level: mins >= 20 ? "red" : "amber" } as const;
}

function fmtWait(mins: number) {
  if (mins < 60) return `${mins}m waiting`;
  return `${Math.round(mins / 60)}h waiting`;
}

function avatarColor(name: string | null): { bg: string; fg: string } {
  const palette = [
    { bg: "rgba(239,68,68,0.22)",   fg: "#fca5a5" },
    { bg: "rgba(59,130,246,0.22)",  fg: "#93c5fd" },
    { bg: "rgba(245,158,11,0.22)",  fg: "#fcd34d" },
    { bg: "rgba(139,92,246,0.22)",  fg: "#c4b5fd" },
    { bg: "rgba(16,185,129,0.22)",  fg: "#6ee7b7" },
    { bg: "rgba(236,72,153,0.22)",  fg: "#f9a8d4" },
    { bg: "rgba(249,115,22,0.22)",  fg: "#fdba74" },
    { bg: "rgba(20,184,166,0.22)",  fg: "#5eead4" },
  ];
  if (!name) return palette[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h << 5) - h + name.charCodeAt(i);
  return palette[Math.abs(h) % palette.length];
}

function WaIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="9" height="9">
      <path d="M12 2C6.48 2 2 6.48 2 12c0 1.85.5 3.58 1.38 5.07L2 22l5.07-1.35C8.47 21.52 10.21 22 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm3.18 14.26c-.26.74-1.5 1.37-2.06 1.45-.55.08-1.24.11-2-.24-2.44-.99-4.02-3.46-4.14-3.62-.12-.16-.97-1.29-.97-2.45 0-1.17.61-1.74.83-1.98.21-.23.47-.29.63-.29.16 0 .32 0 .45.01.14.01.33-.05.52.4.19.45.63 1.54.69 1.65.06.11.1.24.02.38-.08.14-.12.23-.23.35-.11.12-.24.27-.34.36-.11.09-.23.2-.1.4.13.2.58.89 1.24 1.43.85.72 1.57.94 1.8 1.04.22.1.35.08.48-.05.13-.14.54-.63.69-.85.14-.21.29-.18.49-.1.19.08 1.24.58 1.46.69.22.1.36.16.41.25.05.08.05.5-.2 1.18z"/>
    </svg>
  );
}

export default function ChatFloatingWidget() {
  const [open, setOpen]                  = useState(false);
  const [sessions, setSessions]          = useState<Session[]>([]);
  const [active, setActive]              = useState<Session | null>(null);
  const [messages, setMessages]          = useState<Message[]>([]);
  const [reply, setReply]                = useState("");
  const [sending, setSending]            = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingMsgs, setLoadingMsgs]    = useState(false);
  const [filter, setFilter]              = useState<"all" | "open" | "resolved" | "needs_attention" | "needs_reply">("all");
  const [showFilterDd, setShowFilterDd]  = useState(false);
  const [view, setView]                  = useState<"chat" | "metrics">("chat");
  const prevUnreadRef = useRef(0);
  const [botEnabled, setBotEnabled]      = useState(false);
  const [togglingBot, setTogglingBot]    = useState(false);
  const [search, setSearch]              = useState("");
  const [showInfo, setShowInfo]          = useState(false);
  const [guestInfo, setGuestInfo]        = useState<GuestInfo | null>(null);
  const [loadingInfo, setLoadingInfo]    = useState(false);
  const [pulse, setPulse]                = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesBoxRef = useRef<HTMLDivElement>(null);
  const pollRef        = useRef<ReturnType<typeof setInterval> | null>(null);
  const userScrolledUp = useRef(false);
  const filterDdRef    = useRef<HTMLDivElement>(null);

  const staffName = (() => {
    try { const d: any = jwtDecode(localStorage.getItem("ai2_token") || ""); return d.name || "Staff"; }
    catch { return "Staff"; }
  })();

  const fetchSessions = async () => {
    try {
      const { data } = await axios.get(`${API}/api/chat/sessions`);
      setSessions(data || []);
    } catch { }
    finally { setLoadingSessions(false); }
  };

  const fetchMessages = async (sessionId: string) => {
    setLoadingMsgs(true);
    try {
      const { data } = await axios.get(`${API}/api/chat/sessions/${sessionId}/messages`);
      const raw: Message[] = data || [];
      const seen = new Set<string>();
      const deduped = raw.filter(m => {
        const key = m.id ? String(m.id) : `${m.role}|${m.content}|${m.created_at}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setMessages(deduped);
      setSessions(prev => prev.map(s => s.session_id === sessionId ? { ...s, unread: 0 } : s));
    } catch { }
    finally { setLoadingMsgs(false); }
  };

  const fetchGuestInfo = async (sessionId: string) => {
    setLoadingInfo(true);
    try {
      const { data } = await axios.get(`${API}/api/chat/sessions/${sessionId}/info`);
      setGuestInfo(data);
    } catch { }
    finally { setLoadingInfo(false); }
  };

  const fetchBotStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/api/chat/bot-status`);
      setBotEnabled(data.chat_enabled);
    } catch { }
  };

  const toggleBot = async () => {
    if (togglingBot) return;
    setTogglingBot(true);
    try {
      const { data } = await axios.patch(`${API}/api/chat/bot-toggle`, { enabled: !botEnabled });
      setBotEnabled(data.chat_enabled);
    } catch { }
    finally { setTogglingBot(false); }
  };

  useEffect(() => {
    fetchSessions();
    fetchBotStatus();
    const id = setInterval(fetchSessions, 8000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (filterDdRef.current && !filterDdRef.current.contains(e.target as Node)) setShowFilterDd(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (!active) return;
    fetchMessages(active.session_id);
    pollRef.current = setInterval(() => fetchMessages(active.session_id), 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [active?.session_id]);

  useEffect(() => {
    const el = messagesBoxRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distFromBottom < 120) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const totalUnread     = sessions.filter(s => s.status === "open").reduce((n, s) => n + (s.unread || 0), 0);
  const attentionCount  = sessions.filter(s => s.status === "needs_attention").length;
  const needsReplyCount = sessions.filter(s => s.last_message_role === "user" && s.status === "open").length;

  useEffect(() => {
    if (totalUnread > prevUnreadRef.current && !open) {
      setPulse(true);
      setTimeout(() => setPulse(false), 2100);
    }
    prevUnreadRef.current = totalUnread;
  }, [totalUnread, open]);

  const openSession = (s: Session) => {
    userScrolledUp.current = false;
    setActive(s);
    setMessages([]);
    setReply("");
    setGuestInfo(null);
  };

  const sendReply = async () => {
    if (!reply.trim() || !active || sending) return;
    const text = reply.trim();
    setSending(true);
    setReply("");
    try {
      const { data: msg } = await axios.post(
        `${API}/api/chat/sessions/${active.session_id}/messages`,
        { content: text, sentBy: staffName }
      );
      userScrolledUp.current = false;
      setMessages(prev => [...prev, msg]);
    } catch { setReply(text); }
    finally { setSending(false); }
  };

  const toggleStatus = async () => {
    if (!active) return;
    const newStatus = active.status === "open" ? "resolved" : "open";
    try {
      await axios.patch(`${API}/api/chat/sessions/${active.session_id}`, { status: newStatus });
      const updated = { ...active, status: newStatus as Session["status"] };
      setActive(updated);
      setSessions(prev => prev.map(s => s.session_id === active.session_id ? updated : s));
    } catch { }
  };

  const toggleAi = async () => {
    if (!active) return;
    const newVal = active.ai_enabled === false ? true : false;
    try {
      await axios.patch(`${API}/api/chat/sessions/${active.session_id}`, { ai_enabled: newVal });
      const updated = { ...active, ai_enabled: newVal };
      setActive(updated);
      setSessions(prev => prev.map(s => s.session_id === active.session_id ? updated : s));
    } catch { }
  };

  const handleToggleInfo = () => {
    if (!showInfo && active && !guestInfo) fetchGuestInfo(active.session_id);
    setShowInfo(v => !v);
  };

  const filtered = sessions.filter(s => {
    const matchFilter =
      filter === "all"         ? true :
      filter === "needs_reply" ? (s.last_message_role === "user" && s.status === "open") :
      s.status === filter;
    const matchSearch = !search.trim() ||
      (s.guest_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (s.guest_phone || "").includes(search);
    return matchFilter && matchSearch;
  });

  const sorted = [...filtered].sort((a, b) => {
    const wa = waitingInfo(a), wb = waitingInfo(b);
    const ra = wa?.level === "red" ? 0 : wa?.level === "amber" ? 1 : 2;
    const rb = wb?.level === "red" ? 0 : wb?.level === "amber" ? 1 : 2;
    return ra - rb;
  });

  // Group consecutive same-sender images (within 5 min) into albums
  type DItem =
    | { kind: "msg";   msg: Message }
    | { kind: "album"; msgs: Message[]; role: string; sent_by: string | null; created_at: string };
  const displayItems = useMemo<DItem[]>(() => {
    const items: DItem[] = [];
    const consumed = new Set<number>();
    for (let i = 0; i < messages.length; i++) {
      if (consumed.has(i)) continue;
      const msg = messages[i];
      const media = parseMediaContent(msg.content);
      if (media?.type === "image" && media.url) {
        const group = [msg];
        for (let j = i + 1; j < messages.length; j++) {
          const next = messages[j];
          const nm = parseMediaContent(next.content);
          if (!nm || nm.type !== "image" || !nm.url) break;
          if (next.role !== msg.role) break;
          if (msg.role === "staff" && next.sent_by !== msg.sent_by) break;
          if (Math.abs(new Date(next.created_at).getTime() - new Date(msg.created_at).getTime()) > 5 * 60 * 1000) break;
          group.push(next);
          consumed.add(j);
        }
        if (group.length > 1) {
          items.push({ kind: "album", msgs: group, role: msg.role, sent_by: msg.sent_by, created_at: msg.created_at });
        } else {
          items.push({ kind: "msg", msg });
        }
      } else {
        items.push({ kind: "msg", msg });
      }
    }
    return items;
  }, [messages]);

  const openImage = (url: string) => { if (url) window.open(url, "_blank"); };

  const roleAvatar = (role: string) => {
    if (role === "user") {
      return (
        <div className="msg-avatar msg-avatar-user">
          {(active?.guest_name?.[0] ?? "?").toUpperCase()}
        </div>
      );
    }
    if (role === "assistant") {
      return (
        <div className="msg-avatar msg-avatar-assistant">
          <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15">
            <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7v1a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3v-1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73A2 2 0 0 1 12 2zm-3 12a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm6 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"/>
          </svg>
        </div>
      );
    }
    if (role === "staff") {
      return (
        <div className="msg-avatar msg-avatar-staff">
          <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
            <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
          </svg>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {/* Floating button */}
      <button
        className={`chat-float-btn ${open ? "chat-float-btn--open" : ""} ${pulse ? "has-unread" : ""}`}
        onClick={() => setOpen(v => !v)}
        title="Live Chat"
      >
        {open ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="20" height="20">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        )}
        {!open && (totalUnread > 0 || attentionCount > 0) && (
          <span className="chat-float-badge">
            {attentionCount > 0 ? "!" : totalUnread}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div className="chat-float-overlay" onClick={() => setOpen(false)}>
          <div className="chat-float-panel" onClick={e => e.stopPropagation()}>
            <div className="chat-float-panel-topbar">
              <span className="chat-float-panel-title">
                Live Chat
                {totalUnread > 0 && <span className="chat-unread-badge">{totalUnread}</span>}
                {attentionCount > 0 && (
                  <span style={{ marginLeft: 6, fontSize: 10, background: "rgba(239,68,68,0.15)", color: "#f87171", padding: "2px 7px", borderRadius: 4, fontWeight: 700 }}>
                    {attentionCount} handoff
                  </span>
                )}
              </span>
              <button className="chat-float-close" onClick={() => setOpen(false)}>✕</button>
            </div>

            <div className={`chat-page chat-page--widget${active ? " thread-open" : ""}`}>

              {/* ── Session list ── */}
              <div className="chat-sidebar">
                <div className="chat-sidebar-header">
                  <h2 className="chat-sidebar-title">
                    Conversations
                  </h2>
                  <label className="chat-bot-switch-label" title={botEnabled ? "Bot ON — click to disable" : "Bot OFF — click to enable"}>
                    <span className="chat-bot-switch-text">Bot</span>
                    <span className="chat-bot-switch">
                      <input type="checkbox" checked={botEnabled} onChange={toggleBot} disabled={togglingBot} />
                      <span className="chat-bot-switch-track" />
                    </span>
                  </label>
                </div>

                {/* View toggle */}
                <div className="chat-view-tabs">
                  <button className={`chat-view-tab ${view === "chat" ? "active" : ""}`} onClick={() => setView("chat")}>Conversations</button>
                  <button className={`chat-view-tab ${view === "metrics" ? "active" : ""}`} onClick={() => setView("metrics")}>Metrics</button>
                </div>

                {view === "metrics" ? (
                  <div className="chat-metrics-scroll">
                    <ChatMetricsPanel />
                  </div>
                ) : (
                <>

                <div className="chat-search-wrap">
                  <svg className="chat-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                  </svg>
                  <input
                    className="chat-search-input"
                    placeholder="Search guests…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                </div>

                <div className="chat-filter-row">
                  <div className="chat-filter-primary">
                    {([
                      { key: "all",             label: "All",     icon: null, count: sessions.length },
                      { key: "needs_reply",     label: "Reply",   icon: "↩",  count: needsReplyCount },
                      { key: "needs_attention", label: "Handoff", icon: "⚠",  count: attentionCount },
                    ] as const).map(({ key, label, icon, count }) => (
                      <button key={key} className={`chat-filter-btn ${filter === key ? "active" : ""}`} onClick={() => setFilter(key)}>
                        {icon && <span className="chat-filter-icon">{icon}</span>}
                        {label}
                        {count > 0 && <span className="chat-filter-count">{count}</span>}
                      </button>
                    ))}
                  </div>
                  <div className="chat-filter-dropdown-wrap" ref={filterDdRef}>
                    <button
                      className={`chat-filter-more ${(filter === "open" || filter === "resolved") ? "active" : ""}`}
                      onClick={() => setShowFilterDd(v => !v)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12">
                        <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
                      </svg>
                      {filter === "open" ? "Open" : filter === "resolved" ? "Resolved" : "Filter"}
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="10" height="10">
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </button>
                    {showFilterDd && (
                      <div className="chat-filter-dropdown">
                        {([
                          { key: "open",     label: "Open",     count: sessions.filter(s => s.status === "open").length },
                          { key: "resolved", label: "Resolved", count: sessions.filter(s => s.status === "resolved").length },
                        ] as const).map(({ key, label, count }) => (
                          <button key={key} className={`chat-filter-dropdown-item ${filter === key ? "active" : ""}`}
                            onClick={() => { setFilter(key); setShowFilterDd(false); }}>
                            {label}
                            {count > 0 && <span className="chat-filter-count">{count}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="chat-session-list">
                  {loadingSessions && <div className="chat-empty">Loading…</div>}
                  {!loadingSessions && sorted.length === 0 && (
                    <div className="chat-empty-zero">
                      <div className="chat-empty-zero-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28">
                          <rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="2 8 12 14 22 8"/>
                        </svg>
                      </div>
                      <p className="chat-empty-zero-title">You're all caught up</p>
                      <p className="chat-empty-zero-sub">
                        {filter === "all" ? "No conversations yet" :
                         filter === "needs_reply" ? "No guests waiting for a reply" :
                         filter === "needs_attention" ? "No handoffs need attention" :
                         filter === "open" ? "No open conversations" :
                         "No resolved conversations"}
                      </p>
                    </div>
                  )}
                  {sorted.map(s => {
                    const wait = waitingInfo(s);
                    const av = avatarColor(s.guest_name);
                    return (
                      <button
                        key={s.session_id}
                        className={`chat-session-item ${active?.session_id === s.session_id ? "active" : ""} ${wait ? `waiting-${wait.level}` : ""}`}
                        onClick={() => openSession(s)}
                      >
                        <div className="chat-avatar-wrap">
                          <div
                            className="chat-session-avatar"
                            style={{ background: av.bg, color: av.fg }}
                          >
                            {(s.guest_name?.[0] ?? "?").toUpperCase()}
                          </div>
                          {s.source === "whatsapp" && (
                            <span className="chat-channel-badge chat-channel-whatsapp"><WaIcon /></span>
                          )}
                          {s.source === "website" && (
                            <span className="chat-channel-badge chat-channel-website">
                              <svg viewBox="0 0 24 24" fill="currentColor" width="9" height="9"><circle cx="12" cy="12" r="10"/></svg>
                            </span>
                          )}
                        </div>
                        <div className="chat-session-info">
                          <div className="chat-session-top">
                            <div className="chat-session-name-row">
                              <span className={`chat-session-name ${s.unread > 0 ? "chat-session-name--unread" : ""}`}>
                                {s.guest_name || "Guest"}
                              </span>
                              {s.guest_status === "arriving" && (
                                <span className="chat-guest-chip arriving">Arriving</span>
                              )}
                              {s.guest_status === "inhouse" && (
                                <span className="chat-guest-chip inhouse">
                                  {s.room_number ? `Room ${s.room_number}` : "In House"}
                                </span>
                              )}
                            </div>
                            {wait ? (
                              <span className="chat-session-wait-label">{fmtWait(wait.mins)}</span>
                            ) : (
                              <span className="chat-session-time">{timeAgo(s.updated_at)}</span>
                            )}
                          </div>
                          <div className={`chat-session-preview ${s.last_message_role === "user" ? "preview-needs-reply" : ""}`}>
                            {previewText(s.last_message, s.last_message_role)}
                          </div>
                        </div>
                        {s.unread > 0 && <span className="chat-session-unread">{s.unread}</span>}
                      </button>
                    );
                  })}
                </div>

                </>
                )}
              </div>

              {/* ── Thread ── */}
              {!active ? (
                <div className="chat-empty-state">
                  <div className="chat-empty-state-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="38" height="38">
                      <rect x="2" y="4" width="20" height="16" rx="2.5"/>
                      <polyline points="2 8 12 14 22 8"/>
                    </svg>
                  </div>
                  <p className="chat-empty-title">Select a conversation</p>
                  <p className="chat-empty-sub">Pick a guest from the list to view messages</p>
                </div>
              ) : (
                <div className="chat-thread">
                  {active.status === "needs_attention" && active.handoff_briefing && (
                    <div className="chat-handoff-banner">
                      <span className="chat-handoff-banner-label">⚠ Handoff — {active.handoff_reason || "operator needed"}</span>
                      <p className="chat-handoff-banner-briefing">{active.handoff_briefing}</p>
                    </div>
                  )}
                  <div className="chat-thread-header">
                    <button className="chat-back-btn" onClick={() => setActive(null)}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13">
                        <polyline points="15 18 9 12 15 6"/>
                      </svg>
                      Back
                    </button>
                    <div className="chat-thread-avatar">
                      {(active.guest_name?.[0] ?? "?").toUpperCase()}
                    </div>
                    <div className="chat-thread-info">
                      <p className="chat-thread-name">{active.guest_name || "Guest"}</p>
                      <p className="chat-thread-meta">
                        {active.guest_phone && <span>{active.guest_phone}</span>}
                        <span> · via {active.source}</span>
                      </p>
                    </div>
                    <div className="chat-thread-actions">
                      <button className="chat-info-toggle" onClick={handleToggleInfo} title="Guest info">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                          <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                        </svg>
                        Info
                      </button>
                      <button
                        className={`chat-bot-toggle ${active.ai_enabled !== false ? "bot-on" : "bot-off"}`}
                        onClick={toggleAi}
                      >
                        <span className="chat-bot-dot" />
                        {active.ai_enabled !== false ? "AI ON" : "AI OFF"}
                      </button>
                      <button
                        className={`chat-status-btn ${active.status === "open" ? "chat-btn-resolve" : "chat-btn-reopen"}`}
                        onClick={toggleStatus}
                      >
                        {active.status === "open" ? "Mark Resolved" : "Reopen"}
                      </button>
                    </div>
                  </div>

                  <div
                    className="chat-messages"
                    ref={messagesBoxRef}
                    onScroll={() => {
                      const el = messagesBoxRef.current;
                      if (!el) return;
                      userScrolledUp.current = el.scrollHeight - el.scrollTop - el.clientHeight > 80;
                    }}
                  >
                    {loadingMsgs && messages.length === 0 && <div className="chat-loading">Loading messages…</div>}
                    {!loadingMsgs && messages.length === 0 && <div className="chat-no-messages">No messages yet</div>}
                    {displayItems.map((item, i) => {
                      const prevTs = i === 0 ? null : (displayItems[i - 1].kind === "msg" ? (displayItems[i - 1] as { msg: Message }).msg.created_at : (displayItems[i - 1] as { created_at: string }).created_at);
                      const curTs = item.kind === "msg" ? item.msg.created_at : item.created_at;
                      const showDate = i === 0 || !prevTs || !sameDay(prevTs, curTs);

                      if (item.kind === "album") {
                        const imgs = item.msgs.map(m => {
                          const md = parseMediaContent(m.content);
                          return { url: md?.url ?? "", caption: md?.caption };
                        });
                        return (
                          <div key={`album-${item.msgs[0].id}`}>
                            {showDate && <div className="chat-date-sep"><span>{fmtDate(curTs)}</span></div>}
                            <div className={`chat-bubble-row chat-bubble-${item.role}`}>
                              {item.role === "user" && roleAvatar("user")}
                              <div className={`chat-bubble chat-bubble-inner-${item.role}`}>
                                {item.role === "staff" && item.sent_by && <span className="chat-sent-by">{item.sent_by}</span>}
                                <AlbumGrid msgs={imgs} onImageClick={openImage} />
                                <span className="chat-bubble-time">{fmtTime(item.created_at)}</span>
                              </div>
                              {item.role === "assistant" && roleAvatar("assistant")}
                              {item.role === "staff" && roleAvatar("staff")}
                            </div>
                          </div>
                        );
                      }

                      const msg = item.msg;
                      const media = parseMediaContent(msg.content);
                      return (
                        <div key={msg.id}>
                          {showDate && <div className="chat-date-sep"><span>{fmtDate(msg.created_at)}</span></div>}
                          <div className={`chat-bubble-row chat-bubble-${msg.role}`}>
                            {msg.role === "user" && roleAvatar("user")}
                            <div className={`chat-bubble chat-bubble-inner-${msg.role}`}>
                              {msg.role === "staff" && msg.sent_by && <span className="chat-sent-by">{msg.sent_by}</span>}
                              {media ? <MessageMedia media={media} onImageClick={openImage} /> : <p>{msg.content}</p>}
                              <span className="chat-bubble-time">{fmtTime(msg.created_at)}</span>
                            </div>
                            {msg.role === "assistant" && roleAvatar("assistant")}
                            {msg.role === "staff" && roleAvatar("staff")}
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className={`chat-reply-bar ${active.status === "resolved" ? "chat-reply-disabled" : ""}`}>
                    {active.status === "resolved" ? (
                      <p className="chat-resolved-note">Conversation resolved. Reopen to reply.</p>
                    ) : (
                      <>
                        <textarea
                          className="chat-reply-input"
                          placeholder="Type a reply… (Enter to send)"
                          value={reply}
                          rows={2}
                          onChange={e => setReply(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); }
                          }}
                        />
                        <button
                          className="chat-reply-send"
                          disabled={!reply.trim() || sending}
                          onClick={sendReply}
                        >
                          {sending ? "…" : "Send"}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* ── Guest info panel ── */}
              {active && showInfo && (
                <div className="chat-info-panel">
                  <div className="chat-info-header">
                    <span>Guest Info</span>
                    <button className="chat-info-close" onClick={() => setShowInfo(false)}>✕</button>
                  </div>
                  <div className="chat-info-avatar-wrap">
                    <div className="chat-info-avatar">{(active.guest_name?.[0] ?? "?").toUpperCase()}</div>
                    <p className="chat-info-name">{active.guest_name || "Guest"}</p>
                    <p className="chat-info-phone">{active.guest_phone || "—"}</p>
                    <span className={`chat-source-badge chat-source-${active.source}`}>{active.source}</span>
                  </div>
                  {loadingInfo ? (
                    <p className="chat-info-loading">Loading booking…</p>
                  ) : guestInfo?.booking ? (
                    <div className="chat-info-booking">
                      <p className="chat-info-section-title">Booking</p>
                      <div className="chat-info-row"><span>Ref</span><span>#{String(guestInfo.booking.bookingid).slice(-6)}</span></div>
                      {guestInfo.booking.roomnumber && (
                        <div className="chat-info-row"><span>Room</span><span>{guestInfo.booking.roomnumber}</span></div>
                      )}
                      <div className="chat-info-row">
                        <span>Check-in</span>
                        <span>{new Date(guestInfo.booking.checkin).toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" })}</span>
                      </div>
                      <div className="chat-info-row">
                        <span>Check-out</span>
                        <span>{new Date(guestInfo.booking.checkout).toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" })}</span>
                      </div>
                      <div className="chat-info-row">
                        <span>Status</span>
                        <span className={`chat-info-status chat-info-status--${guestInfo.booking.status}`}>{guestInfo.booking.status}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="chat-info-no-booking">No booking found for this guest.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
